import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Twitter, Send, ArrowLeft, Loader2, Eye, EyeOff, ShieldAlert, Cpu, Zap, Globe } from "lucide-react";

// Helper Component for Password Toggle with Glowing Focus
const PasswordInput = ({ placeholder, onChange, required, value }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative flex items-center group">
      <input
        type={show ? "text" : "password"}
        className="w-full bg-[#0a121a]/80 border-2 border-white/5 rounded-xl px-5 py-3 text-white placeholder:text-slate-700 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400/10 focus:outline-none transition-all pr-12 group-hover:border-white/20 font-mono text-xs"
        placeholder={placeholder}
        onChange={onChange}
        required={required}
        value={value}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-4 text-slate-500 hover:text-cyan-400 transition-colors"
      >
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
};

const SocialMedia = () => {
  const [activeTab, setActiveTab] = useState("selection");
  const [loading, setLoading] = useState(false);

  const [twitterData, setTwitterData] = useState({
    twitter_api_key: "", twitter_api_secret: "", twitter_access_token: "",
    twitter_access_token_secret: "", twitter_bearer_token: "", openai_api_key: "-",
    query: "", include_image: true,
  });

  const [telegramData, setTelegramData] = useState({
    openai_api_key: "", telegram_token: "", telegram_chat_id: "", topic: "",
  });

  const handleTwitterSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_AI_BACKEND_URL}/twitter`, twitterData);
      toast.success("Twitter campaign started!");
    } catch (err) { toast.error("Twitter post failed"); } finally { setLoading(false); }
  };

  const handleTelegramSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_AI_BACKEND_URL}/telegram`, telegramData);
      toast.success("Telegram message sent!");
    } catch (err) { toast.error("Telegram send failed"); } finally { setLoading(false); }
  };

  const CyberBackground = () => (
    <>
      <style>{`
        @keyframes flow-bg { 0% { background-position: 0% 0%; } 100% { background-position: 100% 100%; } }
        @keyframes pulse-glow { 0%, 100% { opacity: 0.3; } 50% { opacity: 0.6; } }
        @keyframes slide-line { 0% { transform: translateY(-100%); } 100% { transform: translateY(100vh); } }
      `}</style>
      <div className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(#22d3ee 1px, transparent 1px), linear-gradient(90deg, #22d3ee 1px, transparent 1px)`,
          backgroundSize: '50px 50px',
          maskImage: 'radial-gradient(circle at center, black, transparent 80%)'
        }}
      />
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-0 left-1/4 w-[1px] h-full bg-cyan-500/20 shadow-[0_0_15px_#22d3ee] animate-[slide-line_6s_linear_infinite]" />
        <div className="absolute top-0 left-3/4 w-[1px] h-full bg-blue-500/20 shadow-[0_0_15px_#3b82f6] animate-[slide-line_9s_linear_infinite]" />
      </div>
      <div className="absolute top-[-20%] left-[-10%] w-[800px] h-[800px] bg-cyan-600/10 blur-[150px] rounded-full animate-pulse" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] bg-blue-900/10 blur-[120px] rounded-full" />
    </>
  );

  if (activeTab === "selection") {
    return (
      <div className="min-h-screen bg-[#01040a] p-4 md:p-8 font-sans text-white relative overflow-hidden flex items-center">
        <CyberBackground />
        
        <div className="max-w-4xl mx-auto w-full relative z-10">
          {/* Header section with increased bottom margin */}
          <div className="mb-20">
            <div className="flex items-center gap-4 mb-4 animate-in slide-in-from-left duration-700">
              <div className="w-12 h-12 bg-[#0a121a] border-2 border-cyan-400 rounded-xl flex items-center justify-center shadow-[0_0_30px_rgba(34,211,238,0.2)] rotate-3">
                <Globe size={24} className="text-cyan-400" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-black tracking-tighter uppercase italic">
                  Social <span className="text-cyan-400">Hub</span>
                </h1>
                <p className="text-cyan-400/60 text-[8px] font-black tracking-[0.4em] uppercase">AI Broadcast Network v2.0</p>
              </div>
            </div>
            <p className="text-slate-400 text-base font-light italic border-l-2 border-cyan-500/40 pl-6 max-w-xl">
              Establish high-frequency neural links with global social platforms.
            </p>
          </div>
          
          {/* Grid with increased gap */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Twitter Card */}
            <button
              onClick={() => setActiveTab("twitter")}
              className="group relative p-8 bg-[#0f172a]/80 border-2 border-white/5 rounded-[2.5rem] transition-all duration-500 hover:border-cyan-400 hover:shadow-[0_0_40px_rgba(34,211,238,0.15)] hover:scale-[1.02] text-left overflow-hidden backdrop-blur-xl"
            >
              <div className="absolute top-0 right-0 p-6 opacity-[0.05] group-hover:opacity-100 transition-all duration-700">
                <Twitter size={80} className="text-cyan-400" />
              </div>
              <div className="relative z-10">
                <div className="w-14 h-14 bg-cyan-500/10 border-2 border-cyan-400/30 rounded-xl flex items-center justify-center mb-6 group-hover:bg-cyan-400 transition-all">
                  <Twitter className="w-6 h-6 text-cyan-400 group-hover:text-[#01040a]" />
                </div>
                <h3 className="text-2xl font-black text-white group-hover:text-cyan-400 tracking-tighter uppercase italic">X Protocol</h3>
                <p className="text-slate-400 mt-2 leading-relaxed font-medium text-sm">Automate threads and engagement with LLM logic.</p>
                <div className="mt-8 flex items-center gap-2 text-[10px] font-black text-cyan-400 uppercase tracking-[0.2em]">
                   <Zap size={12} fill="currentColor" /> Configure Agent →
                </div>
              </div>
            </button>

            {/* Telegram Card */}
            <button
              onClick={() => setActiveTab("telegram")}
              className="group relative p-8 bg-[#0f172a]/80 border-2 border-white/5 rounded-[2.5rem] transition-all duration-500 hover:border-blue-400 hover:shadow-[0_0_40px_rgba(59,130,246,0.15)] hover:scale-[1.02] text-left overflow-hidden backdrop-blur-xl"
            >
              <div className="absolute top-0 right-0 p-6 opacity-[0.05] group-hover:opacity-100 transition-all duration-700">
                <Send size={80} className="text-blue-400" />
              </div>
              <div className="relative z-10">
                <div className="w-14 h-14 bg-blue-500/10 border-2 border-blue-400/30 rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-500 transition-all">
                  <Send className="w-6 h-6 text-blue-400 group-hover:text-white" />
                </div>
                <h3 className="text-2xl font-black text-white group-hover:text-blue-400 tracking-tighter uppercase italic">Telegram Node</h3>
                <p className="text-slate-400 mt-2 leading-relaxed font-medium text-sm">Broadcast intelligence streams to channels.</p>
                <div className="mt-8 flex items-center gap-2 text-[10px] font-black text-blue-400 uppercase tracking-[0.2em]">
                   <Zap size={12} fill="currentColor" /> Configure Agent →
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#01040a] p-4 md:p-8 text-white relative overflow-hidden flex items-center justify-center">
      <CyberBackground />

      <div className="max-w-2xl mx-auto w-full relative z-10 animate-in zoom-in duration-500">
        <button 
          onClick={() => setActiveTab("selection")}
          className="group flex items-center gap-2 text-slate-500 hover:text-cyan-400 mb-6 transition-all font-black uppercase text-[10px] tracking-[0.3em]"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> Return to Hub
        </button>

        <div className="bg-[#0f172a]/90 rounded-[2rem] border-2 border-white/10 shadow-[0_0_40px_rgba(0,0,0,0.6)] overflow-hidden backdrop-blur-3xl">
          <div className={`p-6 ${activeTab === 'twitter' ? 'bg-cyan-500/10' : 'bg-blue-600/10'} border-b border-white/10 relative`}>
             <div className="flex items-center gap-3 mb-1">
                <Cpu className="text-cyan-400" size={20} />
                <h2 className="text-2xl font-black italic uppercase tracking-tighter">{activeTab} Setup</h2>
             </div>
             <p className="text-slate-400 text-[11px] font-medium tracking-wide">Configure identity tokens for the agent.</p>
          </div>

          <form onSubmit={activeTab === 'twitter' ? handleTwitterSubmit : handleTelegramSubmit} className="p-8 space-y-5">
            {activeTab === 'twitter' ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1">Twitter API Key</label>
                    <PasswordInput placeholder="Consumer Key" onChange={e => setTwitterData({...twitterData, twitter_api_key: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1">Twitter API Secret</label>
                    <PasswordInput placeholder="Consumer Secret" onChange={e => setTwitterData({...twitterData, twitter_api_secret: e.target.value})} required />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1">Access Token</label>
                    <PasswordInput placeholder="User Token" onChange={e => setTwitterData({...twitterData, twitter_access_token: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1">Access Secret</label>
                    <PasswordInput placeholder="Token Secret" onChange={e => setTwitterData({...twitterData, twitter_access_token_secret: e.target.value})} required />
                  </div>
                </div>
                <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1 flex items-center gap-2">
                      <ShieldAlert size={10} className="text-cyan-500" /> OpenAI API Key
                    </label>
                    <PasswordInput placeholder="sk-..." onChange={e => setTwitterData({...twitterData, openai_api_key: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <label className="text-[9px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1">Campaign Query Target</label>
                  <textarea 
                    placeholder="e.g. Latest trends..." 
                    className="w-full bg-[#0a121a]/80 border-2 border-white/5 rounded-xl px-5 py-3 text-white placeholder:text-slate-700 focus:border-cyan-400 focus:outline-none transition-all h-24 resize-none font-mono text-xs" 
                    onChange={e => setTwitterData({...twitterData, query: e.target.value})} 
                    required 
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[9px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1">OpenAI Core Key</label>
                  <PasswordInput placeholder="sk-..." onChange={e => setTelegramData({...telegramData, openai_api_key: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <label className="text-[9px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1">Bot API Token</label>
                  <PasswordInput placeholder="7823...:AAH..." onChange={e => setTelegramData({...telegramData, telegram_token: e.target.value})} required />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1">Chat Identifier</label>
                    <input type="text" className="w-full bg-[#0a121a]/80 border-2 border-white/5 rounded-xl px-5 py-3 text-white placeholder:text-slate-700 focus:border-blue-400 focus:outline-none transition-all font-mono text-xs" placeholder="-100..." onChange={e => setTelegramData({...telegramData, telegram_chat_id: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase text-slate-500 tracking-[0.2em] ml-1">Primary Topic</label>
                    <input className="w-full bg-[#0a121a]/80 border-2 border-white/5 rounded-xl px-5 py-3 text-white placeholder:text-slate-700 focus:border-blue-400 focus:outline-none transition-all font-mono text-xs" placeholder="Topic" onChange={e => setTelegramData({...telegramData, topic: e.target.value})} required />
                  </div>
                </div>
              </div>
            )}

            <button
              disabled={loading}
              className={`w-full py-4 rounded-xl font-black text-[10px] uppercase tracking-[0.3em] transition-all active:scale-[0.98] disabled:opacity-50 mt-4 flex items-center justify-center relative overflow-hidden group
                ${activeTab === 'twitter' ? 'bg-cyan-500 text-[#01040a]' : 'bg-blue-600 text-white'}`}
            >
              <span className="relative z-10 flex items-center gap-2">
                {loading ? <Loader2 className="animate-spin" size={16} /> : <Zap size={14} fill="currentColor" />}
                Sync Node
              </span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SocialMedia;